import React, { useEffect, useState } from 'react';
import { Clock, Plus, Trash2, CalendarOff, Settings, HelpCircle } from 'lucide-react';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import api from '../services/api';
import { useToast } from '../hooks/use-toast';

type ModoHorario = 'ABERTO_24H' | 'AGENDADO' | 'FECHADO';

interface HorarioDia {
  diaSemana: number;
  modo: ModoHorario;
  horaInicio: string | null;
  horaFim: string | null;
}

interface Feriado {
  id: string;
  nome: string;
  data: string;
  ativo: boolean;
}

interface ConfiguracaoMensagem {
  ativarMensagemForaHorario: boolean;
  mensagemForaHorario: string | null;
  limiteEnviosForaHorario: number;
}

const DIAS_LABEL = ['Domingo', 'Segunda-Feira', 'Terça-Feira', 'Quarta-Feira', 'Quinta-Feira', 'Sexta-Feira', 'Sábado'];

const extractError = (e: any) => e?.response?.data?.message || e?.response?.data?.error || e?.message || 'Erro inesperado';

const HorarioAtendimentoConfig: React.FC = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [horarios, setHorarios] = useState<HorarioDia[]>([]);
  const [feriados, setFeriados] = useState<Feriado[]>([]);
  const [configMensagem, setConfigMensagem] = useState<ConfiguracaoMensagem>({
    ativarMensagemForaHorario: false,
    mensagemForaHorario: '',
    limiteEnviosForaHorario: 1,
  });
  const [novoFeriado, setNovoFeriado] = useState({ nome: '', data: '' });
  const [salvandoFeriado, setSalvandoFeriado] = useState(false);

  const carregar = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/horario-atendimento');
      setHorarios(data.horarios.map((h: any) => ({
        diaSemana: h.diaSemana,
        modo: h.modo,
        horaInicio: h.horaInicio,
        horaFim: h.horaFim,
      })));
      setFeriados(data.feriados);
      setConfigMensagem({
        ativarMensagemForaHorario: !!data.configuracaoMensagem.ativarMensagemForaHorario,
        mensagemForaHorario: data.configuracaoMensagem.mensagemForaHorario || '',
        limiteEnviosForaHorario: data.configuracaoMensagem.limiteEnviosForaHorario || 1,
      });
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atualizarDia = (diaSemana: number, patch: Partial<HorarioDia>) => {
    setHorarios((prev) => prev.map((h) => (h.diaSemana === diaSemana ? { ...h, ...patch } : h)));
  };

  const handleSalvar = async () => {
    setSalvando(true);
    try {
      const horariosInvalidos = horarios.some((h) => h.modo === 'AGENDADO' && (!h.horaInicio || !h.horaFim));
      if (horariosInvalidos) {
        toast({ title: 'Atenção', description: 'Informe o horário de início e fim para os dias em modo Agendado.', variant: 'destructive' });
        setSalvando(false);
        return;
      }

      await api.put('/horario-atendimento/horarios', { horarios });
      await api.put('/horario-atendimento/mensagem', configMensagem);

      toast({ title: 'Sucesso', description: 'Horário de atendimento salvo com sucesso.' });
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    } finally {
      setSalvando(false);
    }
  };

  const handleAdicionarFeriado = async () => {
    if (!novoFeriado.nome.trim() || !novoFeriado.data) {
      toast({ title: 'Atenção', description: 'Informe o nome e a data do feriado.', variant: 'destructive' });
      return;
    }
    setSalvandoFeriado(true);
    try {
      const { data } = await api.post('/horario-atendimento/feriados', {
        nome: novoFeriado.nome.trim(),
        data: novoFeriado.data,
        ativo: true,
      });
      setFeriados((prev) => [...prev, data.feriado].sort((a, b) => a.data.localeCompare(b.data)));
      setNovoFeriado({ nome: '', data: '' });
      toast({ title: 'Sucesso', description: 'Feriado adicionado com sucesso.' });
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    } finally {
      setSalvandoFeriado(false);
    }
  };

  const handleToggleFeriado = async (feriado: Feriado) => {
    const ativo = !feriado.ativo;
    setFeriados((prev) => prev.map((f) => (f.id === feriado.id ? { ...f, ativo } : f)));
    try {
      await api.put(`/horario-atendimento/feriados/${feriado.id}`, { nome: feriado.nome, data: feriado.data, ativo });
    } catch (e) {
      setFeriados((prev) => prev.map((f) => (f.id === feriado.id ? { ...f, ativo: feriado.ativo } : f)));
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    }
  };

  const handleExcluirFeriado = async (feriado: Feriado) => {
    try {
      await api.delete(`/horario-atendimento/feriados/${feriado.id}`);
      setFeriados((prev) => prev.filter((f) => f.id !== feriado.id));
      toast({ title: 'Sucesso', description: 'Feriado excluído com sucesso.' });
    } catch (e) {
      toast({ title: 'Erro', description: extractError(e), variant: 'destructive' });
    }
  };

  if (loading) {
    return <div className="p-6 text-gray-500 dark:text-gray-400">Carregando...</div>;
  }

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2">
          <h1 className="text-xl sm:text-2xl font-bold dark:text-white">Horário de Atendimento</h1>
          <HelpCircle className="h-4 w-4 text-gray-400" />
        </div>
        <Button onClick={handleSalvar} disabled={salvando}>
          <Settings className="h-4 w-4 mr-2" />
          {salvando ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
      <p className="text-gray-500 dark:text-gray-400 mb-6">Configure os horários de atendimento e feriados</p>

      {/* Horários por dia */}
      <Card className="mb-6 p-4 dark:bg-slate-800">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="h-5 w-5" />
          <h3 className="text-lg font-medium dark:text-white">Horários por Dia</h3>
        </div>

        <div className="space-y-3">
          {horarios.map((h) => {
            const label = DIAS_LABEL[h.diaSemana];
            let status = { text: 'Fechado', className: 'text-gray-500 dark:text-gray-400' };
            if (h.modo === 'ABERTO_24H') {
              status = { text: 'Aberto 24 horas', className: 'text-emerald-600 dark:text-emerald-400' };
            } else if (h.modo === 'AGENDADO') {
              status = h.horaInicio && h.horaFim
                ? { text: `Aberto das ${h.horaInicio} às ${h.horaFim}`, className: 'text-emerald-600 dark:text-emerald-400' }
                : { text: 'Defina o horário de início e fim', className: 'text-amber-600 dark:text-amber-400' };
            }

            return (
              <div key={h.diaSemana} className="border border-gray-200 dark:border-slate-700 rounded-lg p-4">
                <div className="font-medium mb-2 dark:text-white">{label}</div>
                <div className="flex flex-wrap gap-2 mb-2">
                  {(['ABERTO_24H', 'AGENDADO', 'FECHADO'] as ModoHorario[]).map((modo) => (
                    <button
                      key={modo}
                      type="button"
                      onClick={() => atualizarDia(h.diaSemana, { modo })}
                      className={`px-3 py-1.5 rounded-md text-sm font-medium border transition-colors ${
                        h.modo === modo
                          ? 'bg-indigo-600 text-white border-indigo-600'
                          : 'bg-white dark:bg-slate-900 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-slate-600'
                      }`}
                    >
                      {modo === 'ABERTO_24H' ? '24 horas' : modo === 'AGENDADO' ? 'Agendado' : 'Fechado'}
                    </button>
                  ))}
                </div>

                {h.modo === 'AGENDADO' && (
                  <div className="grid grid-cols-2 gap-3 mb-2 max-w-sm">
                    <div>
                      <Label className="text-xs">Início</Label>
                      <Input
                        type="time"
                        value={h.horaInicio || ''}
                        onChange={(e) => atualizarDia(h.diaSemana, { horaInicio: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label className="text-xs">Fim</Label>
                      <Input
                        type="time"
                        value={h.horaFim || ''}
                        onChange={(e) => atualizarDia(h.diaSemana, { horaFim: e.target.value })}
                      />
                    </div>
                  </div>
                )}

                <div className={`text-sm ${status.className}`}>{status.text}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Mensagem fora do horário */}
      <Card className="mb-6 p-4 dark:bg-slate-800">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="text-lg font-medium dark:text-white">Mensagem Fora do Horário</h3>
          <div className="flex items-center gap-2">
            <Switch
              checked={configMensagem.ativarMensagemForaHorario}
              onCheckedChange={(checked) => setConfigMensagem((prev) => ({ ...prev, ativarMensagemForaHorario: checked }))}
            />
            <Label className="text-sm">Ativar envio automático</Label>
          </div>
        </div>

        <Label className="mb-1 block">Mensagem automática para fora do horário de atendimento</Label>
        <Textarea
          rows={4}
          value={configMensagem.mensagemForaHorario || ''}
          onChange={(e) => setConfigMensagem((prev) => ({ ...prev, mensagemForaHorario: e.target.value }))}
          placeholder="Olá! No momento estamos fora do horário de atendimento. Deixe sua mensagem e retornaremos assim que possível."
          className="mb-4"
        />

        <div className="max-w-xs">
          <Label className="mb-1 block">Quantidade de envios por dia (mesmo contato)</Label>
          <Input
            type="number"
            min={1}
            max={50}
            value={configMensagem.limiteEnviosForaHorario}
            onChange={(e) => setConfigMensagem((prev) => ({ ...prev, limiteEnviosForaHorario: Number(e.target.value) || 1 }))}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Número máximo de vezes que a mensagem automática será enviada ao mesmo contato em um mesmo dia.
          </p>
        </div>
      </Card>

      {/* Feriados */}
      <Card className="mb-6 p-4 dark:bg-slate-800">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div className="flex items-center gap-2">
            <CalendarOff className="h-5 w-5" />
            <h3 className="text-lg font-medium dark:text-white">Feriados</h3>
          </div>
        </div>

        <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 text-amber-800 dark:text-amber-300 text-sm rounded-md p-3 mb-4">
          Os feriados marcados como ativos bloqueiam o atendimento naquele dia.
        </div>

        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <Input
            placeholder="Nome do feriado"
            value={novoFeriado.nome}
            onChange={(e) => setNovoFeriado((prev) => ({ ...prev, nome: e.target.value }))}
          />
          <Input
            type="date"
            value={novoFeriado.data}
            onChange={(e) => setNovoFeriado((prev) => ({ ...prev, data: e.target.value }))}
            className="sm:max-w-[180px]"
          />
          <Button onClick={handleAdicionarFeriado} disabled={salvandoFeriado}>
            <Plus className="h-4 w-4 mr-1" />
            Adicionar
          </Button>
        </div>

        {feriados.length === 0 ? (
          <div className="text-center py-8 text-gray-400 dark:text-gray-500">
            <CalendarOff className="h-8 w-8 mx-auto mb-2" />
            Nenhum feriado configurado
          </div>
        ) : (
          <div className="space-y-2">
            {feriados.map((feriado) => (
              <div key={feriado.id} className="flex items-center justify-between gap-3 border border-gray-200 dark:border-slate-700 rounded-md p-3">
                <div className="min-w-0">
                  <div className="font-medium dark:text-white break-words">{feriado.nome}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {new Date(feriado.data).toLocaleDateString('pt-BR', { timeZone: 'UTC' })}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={feriado.ativo} onCheckedChange={() => handleToggleFeriado(feriado)} />
                  <Button variant="ghost" size="icon" onClick={() => handleExcluirFeriado(feriado)}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default HorarioAtendimentoConfig;
